/* Model-based complementary parts. Explicit model tags take precedence over title codes. */
(function (root) {
  'use strict';
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/ß/g, 'ss').trim();
  const key = (value) => normalize(value).replace(/[^a-z0-9.]+/g, '-').replace(/^-|-$/g, '');
  function describe(product) {
    const tags = (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(normalize);
    const text = normalize([product.title, ...tags].join(' '));
    const explicit = tags.filter(t => /^(adp:)?model:/.test(t)).map(t => key(t.replace(/^(adp:)?model:/, ''))).filter(Boolean);
    const models = explicit.length ? explicit : [...text.matchAll(/\b(w\d{3}|t[4-7](?:\.\d)?)\b/g)].map(m => m[1]);
    // Named generations are safe only when the generation is actually present.
    if (!explicit.length) {
      for (const m of text.matchAll(/\b(transit custom|transit connect|transit|master|trafic|kangoo|caddy|crafter|daily|doblo|ducato|boxer|jumper|expert|jumpy|berlingo|partner|vivaro|movano|combo)\s+(viii|vii|vi|iv|iii|ii|v|i|mk\s*\d+)\b/g)) models.push(key(m[0]));
    }
    let part = tags.find(t => t.startsWith('adp:part:'))?.slice(9);
    if (!['door', 'mirror', 'rear-door', 'tail-light'].includes(part)) {
      // A loose glass, cover, handle or hinge is not a complete door/mirror/light.
      if (/\b(deurpaneel\w*|deurklink\w*|deurgreep\w*|portiergreep\w*|portierbekleding|scharnier\w*|spiegelglas|spiegelkap\w*|spiegelmotor\w*|lampenhouder\w*|mirror glass|mirror cover|door handle|door hinge)\b/.test(text)) return {part:undefined,models:[],side:'',mirrorIncluded:false};
      if (/\b(achterdeur\w*|achterportier\w*|rear doors?|back doors?|hecktur\w*|hintertur\w*|porte arriere\w*)\b/.test(text)) part = 'rear-door';
      else if (/\b(voorportier\w*|voordeur\w*|portier\w*|schuifdeur\w*|deur\w*|front doors?|sliding doors?|vordertur\w*|schiebetur\w*|porte avant\w*|porte coulissante\w*)\b/.test(text)) part = 'door';
      else if (/\b(achterlicht\w*|achterlamp\w*|tail[ -]?lights?|rear lights?|ruckleucht\w*|feu[x]? arriere\w*)\b/.test(text)) part = 'tail-light';
      else if (/\b(buitenspiegel\w*|spiegel\w*|wing mirrors?|side mirrors?|mirror\w*|aussenspiegel\w*|retroviseur\w*)\b/.test(text)) part = 'mirror';
    }
    // Do not sell a second mirror for a door explicitly supplied with one.
    const mirrorIncluded = part === 'door' && /\b(met spiegel|inclusief spiegel|with mirror|mit spiegel|avec retroviseur)\b/.test(text);
    const left = /\b(links\w*|left|lh|gauche)\b/.test(text) || /\b(?:voorportier|portier|deur|spiegel|achterlicht)\s+l\b/.test(text);
    const right = /\b(rechts\w*|rechter\w*|right|rh|droit\w*)\b/.test(text) || /\b(?:voorportier|portier|deur|spiegel|achterlicht)\s+r\b/.test(text);
    return {part, models: [...new Set(models)], side: left && right ? 'both' : left ? 'left' : right ? 'right' : '', mirrorIncluded};
  }
  function matches(source, candidate, excluded = []) {
    if (String(source.id) === String(candidate.id) || excluded.map(String).includes(String(candidate.id)) || !candidate.available) return false;
    const a = describe(source), b = describe(candidate);
    const pairs = {door: 'mirror', mirror: 'door', 'rear-door': 'tail-light', 'tail-light': 'rear-door'};
    if (!a.part || pairs[a.part] !== b.part || a.mirrorIncluded || b.mirrorIncluded) return false;
    if (!a.models.some(m => b.models.includes(m))) return false;
    // A known left/right source requires a compatible, specified side.
    if (a.side && a.side !== 'both' && b.side !== a.side && b.side !== 'both') return false;
    return true;
  }
  const matcher = {describe, matches};
  if (typeof module !== 'undefined' && module.exports) module.exports = matcher;
  if (!root?.customElements || root.customElements.get('adp-cross-sell')) return;
  root.ADPCrossSellMatcher = matcher;
  const cache = new Map();
  async function search(url) {
    if (!cache.has(url)) cache.set(url, (async () => {
      const response = await fetch(url, {headers: {Accept: 'text/html'}});
      if (!response.ok) throw new Error('Search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = doc.querySelector('[data-adp-results]');
      if (!payload) throw new Error('Search unavailable');
      return JSON.parse(payload.textContent);
    })().catch(error => { cache.delete(url); throw error; }));
    return cache.get(url);
  }
  class ADPCrossSell extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.data = JSON.parse(this.querySelector('[data-adp-config]').textContent);
      this.list = this.querySelector('[data-adp-list]');
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-adp-add]');
        if (button) this.add(button);
      });
      if (typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.dataset.context === 'product') this.load(true);
      });
      if (this.dataset.context === 'drawer') {
        this.observer = new MutationObserver(() => {if (this.closest('cart-drawer')?.classList.contains('active')) this.load();});
        this.observer.observe(this.closest('cart-drawer'), {attributes:true, attributeFilter:['class']});
        if (this.closest('cart-drawer')?.classList.contains('active')) this.load();
      } else this.load();
    }
    disconnectedCallback() {this.observer?.disconnect(); this.unsubscribe?.(); this.version = (this.version || 0) + 1;}
    async load(refresh = false) {
      if (this.loaded && !refresh) return;
      this.loaded = true;
      const version = this.version = (this.version || 0) + 1;
      try {
        let excluded = this.data.cartIds;
        if (this.dataset.context === 'product') {
          const cartResponse = await fetch(this.data.root + 'cart.js');
          if (!cartResponse.ok) throw new Error('Cart unavailable');
          excluded = (await cartResponse.json()).items.map(item => item.product_id);
        }
        const sources = this.data.sources.filter(p => describe(p).part && describe(p).models.length && !describe(p).mirrorIncluded);
        const models = [...new Set(sources.flatMap(p => describe(p).models))];
        if (!models.length) {this.hidden = true; return;}
        const all = [];
        // Sequential, small searches, cached per model. Never download the whole catalog.
        for (const model of models) {
          const query = `(title:"${model.replace(/-/g, ' ')}" OR tag:"${model}" OR tag:"model:${model}" OR tag:"adp:model:${model}")`;
          const url = new URL(this.data.searchUrl, location.origin);
          url.search = new URLSearchParams({q:query,type:'product',view:'adp-cross-sell','options[prefix]':'none','options[unavailable_products]':'hide'});
          for (let page = 1; page <= 10; page++) {
            url.searchParams.set('page', page);
            const data = await search(url.href);
            all.push(...data.products);
            if (!data.next) break;
          }
        }
        if (!this.isConnected || version !== this.version) return;
        const seen = new Set();
        this.products = all.filter(p => {
          if (seen.has(p.id) || !sources.some(s => matches(s, p, excluded))) return false;
          seen.add(p.id); return true;
        }).slice(0, 3);
        this.list.replaceChildren(...this.products.map(p => this.card(p)));
        this.hidden = !this.products.length;
      } catch (_) { if (version === this.version) {this.hidden = true; this.loaded = false;} }
    }
    card(product) {
      const card = document.createElement('article'); card.className = 'adp-cross-sell__card';
      const link = document.createElement('a'); link.href = product.url; link.className = 'adp-cross-sell__product';
      if (product.image) {
        const img = document.createElement('img'); img.src = product.image; img.alt = ''; img.width = 72; img.height = 72; img.loading = 'lazy'; link.append(img);
      }
      const info = document.createElement('span'); info.className = 'adp-cross-sell__info';
      const title = document.createElement('span'); title.className = 'adp-cross-sell__title'; title.textContent = product.title;
      const price = document.createElement('span'); price.className = 'adp-cross-sell__price'; price.textContent = product.price;
      info.append(title, price); link.append(info); card.append(link);
      if (product.singleVariant && product.variant) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'adp-cross-sell__add';
        button.dataset.adpAdd = String(product.id); button.textContent = this.data.labels.add;
        button.setAttribute('aria-label', this.data.labels.add + ': ' + product.title); card.append(button);
      } else {
        const choose = document.createElement('a'); choose.className = 'adp-cross-sell__add'; choose.href = product.url; choose.textContent = this.data.labels.choose; card.append(choose);
      }
      return card;
    }
    async add(button) {
      if (this.adding) return;
      this.adding = true; button.disabled = true; button.textContent = this.data.labels.adding;
      const status = this.querySelector('[data-adp-status]'); status.textContent = '';
      const product = this.products.find(p => String(p.id) === button.dataset.adpAdd);
      const drawer = document.querySelector('cart-drawer');
      const cartPage = document.querySelector('cart-items');
      const sections = [...new Set([...(drawer?.getSectionsToRender().map(s => s.id) || []),...(cartPage?.getSectionsToRender().map(s => s.section) || [])])].slice(0,5);
      let added = false;
      try {
        const response = await fetch(this.data.root + 'cart/add.js', {
          method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
          body:JSON.stringify({items:[{id:product.variant,quantity:product.quantity || 1}],sections,sections_url:location.pathname})
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || this.data.labels.error);
        added = true;
        // Section failures must never lead to a second add request.
        if (sections.some(s => !data.sections?.[s])) {location.assign(this.data.root + 'cart'); return;}
        drawer?.setActiveElement(document.querySelector('#cart-icon-bubble'));
        if (drawer) {drawer.classList.remove('is-empty'); drawer.renderContents(data);}
        if (cartPage) {
          for (const s of cartPage.getSectionsToRender()) {
            const target = document.getElementById(s.id)?.querySelector(s.selector) || document.getElementById(s.id);
            const parsed = new DOMParser().parseFromString(data.sections[s.section], 'text/html').querySelector(s.selector);
            if (target && parsed) target.innerHTML = parsed.innerHTML;
          }
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {source:'adp-cross-sell',productVariantId:product.variant,cartData:data});
        if (!drawer && !cartPage) location.assign(this.data.root + 'cart');
      } catch (error) {
        if (added) {location.assign(this.data.root + 'cart'); return;}
        status.textContent = error.message || this.data.labels.error;
      } finally {this.adding = false; button.disabled = false; button.textContent = this.data.labels.add;}
    }
  }
  root.customElements.define('adp-cross-sell', ADPCrossSell);
})(typeof window === 'undefined' ? null : window);
